// ============================================================
//  src/data/items.js  —  SimpleMC item registry
//
//  Covers weapons, armour, tools, consumables — anything
//  that needs a detail page.
//
//  To add a new item: copy one of the entries below and
//  fill in your values. Every field except id, name, type
//  and rarity is optional — leave out what you don't need.
// ============================================================

export const ALL_ITEMS = [

  // ── WEAPONS ───────────────────────────────────────────────

  {
    id: 'ashbane-blade',
    name: 'Ashbane Blade',
    category: 'Weapon',
    type: 'Sword',
    rarity: 'legendary',

    description: 'A sword forged from the remnants of an ancient sealed tomb. Burns with pale fire on critical hits and deals bonus damage to all undead mobs.',
    lore: 'The tomb had been sealed for three hundred years. They opened it anyway. The sword was the only thing inside.',

    stats: [
      { label: 'Damage', value: 18, max: 30, unit: ' DMG' },
      { label: 'Attack Speed', value: 8, max: 10, unit: '' },
      { label: 'Durability', value: 24, max: 30, unit: '00' },
      { label: 'vs Undead', value: 25, max: 30, unit: ' DMG', color: 'var(--tier-boss)' },
    ],

    abilities: [
      {
        name: 'Ashfire Crit',
        type: 'Passive — On Crit',
        description: 'Critical hits set the target ablaze for 3 seconds, dealing 2 fire damage per tick. Stacks up to 3 times.',
        cooldown: null,
      },
      {
        name: 'Undead Bane',
        type: 'Passive',
        description: 'Deals +40% damage to all undead mob types including zombies, skeletons, phantoms and The Watcher.',
        cooldown: null,
      },
      {
        name: 'Soul Slash',
        type: 'Active — Right Click',
        description: 'Releases a wave of pale energy in a 6-block cone, dealing 12 damage to all enemies hit. Costs 20 durability per use.',
        cooldown: '15s',
      },
    ],

    recipe: {
      image: null,
      imageAlt: 'Ashbane Blade crafting recipe',
      ingredients: [
        { name: 'Shadow Shard', qty: 2, link: null },
        { name: 'Drake Bone', qty: 1, link: null },
        { name: 'Blaze Rod', qty: 1, link: null },
      ],
      craftingType: 'Crafting Table',
      note: 'Shadow Shards drop from The Watcher boss. Drake Bone drops from the Ridgeback Drake.',
    },

    obtainMethods: [
      'Craft using the recipe above',
      'Rare drop from The Watcher — 12% chance',
    ],

    tags: ['Undead Slayer', 'Fire', 'Craftable', 'Boss Drop'],

    history: [
      { version: 'v1.1', date: 'Feb 10 2026', changes: ['Added to the game'] },
      { version: 'v1.3', date: 'Mar 12 2026', changes: ['Soul Slash cooldown reduced from 20s to 15s', 'Undead Bane bonus increased from 30% to 40%'] },
    ],
  },

  {
    id: 'cinderbow',
    name: 'Cinderbow',
    category: 'Weapon',
    type: 'Bow',
    rarity: 'epic',

    description: 'A bow wrapped in Ember Feathers that sets every arrow ablaze. Fully charged shots detonate on impact, igniting a 3-block radius.',
    lore: 'You can smell it before you see it. Then you see it, and you wish you could not.',

    stats: [
      { label: 'Arrow Damage', value: 14, max: 30, unit: ' DMG' },
      { label: 'Fire Damage', value: 8, max: 30, unit: ' DMG', color: 'var(--accent-bright)' },
      { label: 'Explosion Radius', value: 6, max: 10, unit: '' },
      { label: 'Durability', value: 18, max: 30, unit: '00' },
    ],

    abilities: [
      {
        name: 'Burning Arrow',
        type: 'Passive',
        description: 'All fired arrows are automatically lit, dealing +4 fire damage on hit and setting the target ablaze for 2 seconds.',
        cooldown: null,
      },
      {
        name: 'Charged Detonation',
        type: 'Passive — Fully Charged',
        description: 'Fully charged shots (hold for 1.5s) explode on impact, igniting all blocks and mobs in a 3-block radius and dealing 12 splash damage.',
        cooldown: null,
      },
      {
        name: 'Ember Sight',
        type: 'Active — Shift + Right Click',
        description: 'Targets hit while active are highlighted through walls for 6 seconds.',
        cooldown: '45s',
      },
    ],

    recipe: {
      image: null,
      imageAlt: 'Cinderbow crafting recipe',
      ingredients: [
        { name: 'Ember Feather', qty: 3, link: null },
        { name: 'Bow', qty: 1, link: null },
        { name: 'Blaze Rod', qty: 1, link: null },
      ],
      craftingType: 'Crafting Table',
      note: 'Ember Feathers drop from Cinderbirds in the Scorched Wastes.',
    },

    obtainMethods: [
      'Craft using the recipe above',
      'Supply Drop crate — rare chance',
    ],

    tags: ['Fire', 'AOE', 'Craftable', 'Ranged'],

    history: [
      { version: 'v1.1', date: 'Feb 10 2026', changes: ['Added to the game'] },
      { version: 'v1.1', date: 'Feb 10 2026', changes: ['Explosion radius nerfed from 6 to 4 blocks'] },
      { version: 'v1.3', date: 'Mar 12 2026', changes: ['Explosion radius restored to 6 blocks', 'Ember Sight cooldown increased from 30s to 45s'] },
    ],
  },

  {
    id: 'ironback-axe',
    name: 'Ironback Axe',
    category: 'Weapon',
    type: 'Axe',
    rarity: 'rare',

    description: 'A brutal axe forged from Ironback Spider carapace. Ignores 30% of enemy armour — effective against heavily armoured mobs and players.',
    lore: 'The spiders do not like that you made an axe out of their cousin.',

    stats: [
      { label: 'Damage', value: 19, max: 30, unit: ' DMG' },
      { label: 'Armour Pierce', value: 9, max: 10, unit: '' },
      { label: 'Attack Speed', value: 5, max: 10, unit: '' },
      { label: 'Durability', value: 21, max: 30, unit: '00' },
    ],

    abilities: [
      {
        name: 'Armour Shred',
        type: 'Passive',
        description: 'Every hit ignores 30% of the target\'s total armour value. Stacks with Smite and Sharpness enchantments.',
        cooldown: null,
      },
      {
        name: 'Carapace Crush',
        type: 'Active — Right Click',
        description: 'Smashes the target, reducing their effective defence by 20% for 8 seconds. Works on players and custom mobs.',
        cooldown: '15s',
      },
    ],

    recipe: {
      image: null,
      imageAlt: 'Ironback Axe crafting recipe',
      ingredients: [
        { name: 'Carapace Shard', qty: 2, link: null },
        { name: 'Iron Silk Thread', qty: 1, link: null },
        { name: 'Iron Ingot', qty: 1, link: null },
        { name: 'Stick', qty: 1, link: null },
      ],
      craftingType: 'Crafting Table',
      note: 'Carapace Shards and Iron Silk Thread drop from Ironback Spiders at Sunken Docks.',
    },

    obtainMethods: [
      'Craft using the recipe above',
    ],

    tags: ['Armour Pierce', 'Craftable', 'Melee'],

    history: [
      { version: 'v1.1', date: 'Feb 10 2026', changes: ['Added to the game'] },
    ],
  },

  // ── ARMOUR ────────────────────────────────────────────────

  {
    id: 'drake-scale-chestplate',
    name: 'Drake Scale Chestplate',
    category: 'Armour',
    type: 'Chestplate',
    rarity: 'legendary',

    description: 'A chestplate forged from the scales of the Ridgeback Drake. The best armour in the game. Part of the 4-piece Drake Scale set.',
    lore: 'The dragon did not give these up willingly.',

    stats: [
      { label: 'Armour', value: 28, max: 30, unit: '' },
      { label: 'Toughness', value: 9, max: 10, unit: '' },
      { label: 'Fire Resist', value: 10, max: 10, unit: '', color: 'var(--accent-bright)' },
      { label: 'Durability', value: 28, max: 30, unit: '00' },
    ],

    abilities: [
      {
        name: 'Drake Hide',
        type: 'Passive — Set Bonus (4 pieces)',
        description: 'Wearing the full Drake Scale set reduces all damage taken by 20% and makes you immune to fire damage.',
        cooldown: null,
      },
      {
        name: 'Ember Ward',
        type: 'Passive — Equipped',
        description: 'While worn, fire damage from all sources is reduced by 50%. Stacks with Fire Resistance potions.',
        cooldown: null,
      },
    ],

    recipe: {
      image: null,
      imageAlt: 'Drake Scale Chestplate crafting recipe',
      ingredients: [
        { name: 'Drake Scale', qty: 8, link: null },
        { name: 'Ember Sac', qty: 1, link: null },
      ],
      craftingType: 'Crafting Table',
      note: 'Drake Scales drop from the Ridgeback Drake at Ridgeback Summit. Ember Sac is also a Drake drop.',
    },

    obtainMethods: [
      'Craft using Drake Scales from the Ridgeback Drake',
    ],

    tags: ['Set Bonus', 'Fire Resist', 'Craftable', 'Best in Slot'],

    history: [
      { version: 'v1.3', date: 'Mar 12 2026', changes: ['Added to the game with the Ridgeback Drake update', 'Full Drake Scale 4-piece set introduced'] },
    ],
  },

  // ── CONSUMABLES ───────────────────────────────────────────

  {
    id: 'velvet-potion',
    name: 'Velvet Potion',
    category: 'Consumable',
    type: 'Potion',
    rarity: 'rare',

    description: 'A shimmering violet brew from Velvet Antler. Grants Strength III and Regeneration II for 3 full minutes — the best combat buff available without donor perks.',
    lore: 'The Duskdeer do not give up their antlers willingly. Worth it.',

    stats: [
      { label: 'Strength', value: 9, max: 10, unit: ' (Str III)' },
      { label: 'Regen', value: 8, max: 10, unit: ' (Reg II)' },
      { label: 'Duration', value: 18, max: 20, unit: '0s' },
    ],

    abilities: [
      {
        name: 'Strength III',
        type: 'On Drink',
        description: 'Grants Strength III for 3 minutes, increasing all melee damage by +9 per hit.',
        cooldown: null,
      },
      {
        name: 'Regeneration II',
        type: 'On Drink',
        description: 'Grants Regeneration II for 3 minutes, restoring 1 HP every second.',
        cooldown: null,
      },
    ],

    recipe: {
      image: null,
      imageAlt: 'Velvet Potion brewing recipe',
      ingredients: [
        { name: 'Velvet Antler', qty: 1, link: null },
        { name: 'Murk Herb', qty: 2, link: null },
        { name: 'Awkward Potion', qty: 1, link: null },
        { name: 'Blaze Powder', qty: 1, link: null },
      ],
      craftingType: 'Brewing Stand',
      note: 'Velvet Antler drops from Duskdeer. Murk Herb grows in Mirewood Swamp.',
    },

    obtainMethods: [
      'Brew using the recipe above',
      'Supply Drop crate — uncommon chance',
    ],

    tags: ['Buff', 'Combat', 'Brewable'],

    history: [
      { version: 'v1.1', date: 'Feb 10 2026', changes: ['Added to the game'] },
      { version: 'v1.3', date: 'Mar 12 2026', changes: ['Duration increased from 2 minutes to 3 minutes'] },
    ],
  },

];

export default ALL_ITEMS;

// ============================================================
//  ITEM DATA SHAPE — copy this to add a new item
// ============================================================
//
// {
//   id:       'my-item-id',          unique, url-safe string
//   name:     'My Item',
//   category: 'Weapon',              Weapon | Armour | Tool | Consumable | Other
//   type:     'Sword',               free text shown as a tag
//   rarity:   'rare',                common | uncommon | rare | epic | legendary | boss
//
//   description: 'Short description shown on list cards and hero.',
//   lore:        'Italic flavour text.',
//
//   stats: [
//     { label: 'Damage', value: 18, max: 30, unit: ' DMG', color: 'optional css color' },
//   ],
//
//   abilities: [
//     { name: 'My Ability', type: 'Passive', description: '...', cooldown: '10s' or null },
//   ],
//
//   recipe: {
//     image:        null,             set to imported image path once you have the image
//     imageAlt:     'Recipe for ...',
//     ingredients:  [
//       { name: 'Item Name', qty: 2, link: null },   link is optional url string
//     ],
//     craftingType: 'Crafting Table', or 'Brewing Stand' etc
//     note:         'Where to get the ingredients.',
//   },
//
//   obtainMethods: ['Craft it', 'Drop from X mob — Y% chance'],
//
//   tags: ['Tag1', 'Tag2'],
//
//   history: [
//     { version: 'v1.0', date: 'Jan 18 2026', changes: ['Added to the game'] },
//     { version: 'v1.2', date: 'Feb 27 2026', changes: ['Damage nerfed 20 -> 18', 'New ability added'] },
//   ],
// }
